import { toNumber, toArray, toPixels, elementInViewport,
         removeClasses, sum, range, html, Position } from './utils';
import { CrosswordType, SquareType, SquareListItem, GridSize,
         NumberModel, ClueModel } from './models';
import { Change, Changes } from './controllers';

type Clue = HTMLDivElement;
type Clues = HTMLDivElement;
type Square = HTMLDivElement;
type ClueText = HTMLSpanElement;

interface Row {
  row: HTMLDivElement;
  cells: Square[];
}

interface Grid extends Array<Row> { };

// All values in pixels
const BORDER_WIDTH: number = 1;
const MAX_FONT_SIZE: number = 14;
const MIN_FONT_SIZE: number = 9;
const BASE_FONT_SIZE: number = 14;
const SQUARE_SIZE_REGULAR: number = 42;
const SQUARE_SIZE_CIS: number = 54;

const SQUARE_CACHE: Map<string, Square> = new Map<string, Square>();

/**
 * Adds `wc-active-*` classes to the provided squares.
 * A reversed selection is signified by the `wc-reversed` class.
 * An attempt to move the active square into the viewport is
 * made after any changes have been performed.
 */
export function highlight(root: HTMLElement, square: Position | null,
                          squares: Position[], reversed: boolean = false): void {
  let classes: string[];

  removeClasses(root, '.wc-active-word', [
      'wc-active-word', 'wc-active-square', 'wc-reversed'
  ]);

  if (square) {
    classes = reversed ? ['wc-reversed'] : [];

    squares.forEach(([row, col]: Position) => {
      if (row === square[0] && col === square[1]) { // is active square
        _getSquare(root, row, col).classList.add(
            ...(classes.concat('wc-active-word', 'wc-active-square')));
      } else {
        _getSquare(root, row, col).classList.add(
            ...(classes.concat('wc-active-word')));
      }
    });

    _setViewport(_getSquare(root, square[0], square[1]));
  }
}

/**
 * Applies a blinking effect to the provided squares lasting
 * for the given duration.
 */
export function blink(root: HTMLElement, squares: Position[],
                      error: boolean = false, duration: number = 250): void {
  squares.forEach(([row, col]: Position) => {
    let counter: number;
    let square: Square = _getSquare(root, row, col);

    if (square.hasAttribute('data-animation-count')) {
      counter = toNumber(square.getAttribute('data-animation-count'));
    } else {
        counter = 0;
    }

    let extra: string = error ? '-error' : '';

    square.classList.add('wc-fade', 'wc-blink' + extra);
    square.setAttribute('data-animation-count', (counter + 1).toString());

    setTimeout(() => {
      counter = toNumber(square.getAttribute('data-animation-count'));
      if (counter <= 1) {
        square.classList.remove('wc-blink' + extra);

        setTimeout(() => {
          square.classList.remove('wc-fade');
        }, duration);
      }
      square.setAttribute('data-animation-count', (counter - 1).toString());
    }, duration);
  });
}

/**
 * Applies a set of changes to the provided crossword.
 * A Change is a record containing a square and a new
 * value for that square.
 */
export function update(element: HTMLElement, changes: Changes): void {
  changes.forEach((change: Change) => {
    let [row, col]: Position = change.position;
    _getSquare(element, row, col).textContent = change.value === '_' ? '' : change.value;
  });
}

/**
 * Render a crossword inside the given wrapper element, and return
 * the root of the rendered element.
 */
export function render(wrapper: HTMLElement, type: CrosswordType, size: GridSize,
                       squares: SquareListItem[]): HTMLDivElement {
  let grid: Grid;
  let fragment: DocumentFragment;
  let root: HTMLDivElement;
  let row: HTMLDivElement;

  grid = _squares(_grid(size), squares);
  fragment = document.createDocumentFragment();
  root = Elements.wrapper();

  for (let i: number = 0; i < grid.length; i++) {
    row = grid[i].row;
    for (let j: number = 0; j < grid[i].cells.length; j++) {
      row.appendChild(grid[i].cells[j]);
    }
    root.appendChild(row);
  }
  fragment.appendChild(root);
  wrapper.appendChild(fragment);

  _resize(root, type, size, squares);

  return root;
}

export function centerViewportAt(root: HTMLElement, square: Position): void {
  _getSquare(root, square[0], square[1]).scrollIntoView({
    block: 'center',
    inline: 'center',
  });
}

// TODO: This code has too many cases in which it behaves suboptimally.
function _setViewport(square: Square): void {
  let x: number = 0;
  let y: number = 0;

  if ( ! elementInViewport(square)) {
    const rect: ClientRect = square.getBoundingClientRect();

    if (rect.top < 0) {
      y = rect.top;
    } else if (rect.bottom > window.innerHeight) {
      y = rect.bottom - window.innerHeight;
    }

    if (rect.left < 0) {
      x = rect.left;
    } else if (rect.right > window.innerWidth) {
      x = rect.right - window.innerWidth;
    }

    window.scrollBy(x, y);
  }
}

function _getAllSquares(root: HTMLElement): Square[] {
  return toArray(root.querySelectorAll('[data-row][data-col]'));
}

function _getSquare(root: HTMLElement, row: number, col: number): Square {
  let square: Square;
  let selector: string = `[data-row="${row}"][data-col="${col}"]`;

  if (SQUARE_CACHE.has(selector)) {
    return <Square>SQUARE_CACHE.get(selector);
  }

  square = <Square>root.querySelector(selector);
  SQUARE_CACHE.set(selector, square);

  return square;
}

function _getCluesBySquare(root: HTMLElement, row: number, col: number): Clue[] {
  return toArray((<Clues>_getSquare(root, row, col).querySelector('.wc-clues')).children);
}

function _grid(size: GridSize): Grid {
  let grid: Grid;
  let row: Row;
  let i: number;
  let j: number;

  grid = [];

  for (i = 0; i < size.rows; i++) {
    row = {'row': Elements.row(), 'cells': []};
    for (j = 0; j < size.cols; j++) {
      row.cells.push(Elements.square(i, j));
    }

    grid[i] = row;
  }

  return grid;
}

function _squares(grid: Grid, squares: SquareListItem[]): Grid {
  squares.forEach(({row, col, square}: SquareListItem) => {
    let cell: Square;
    let numberSquare: NumberModel;

    cell = grid[row].cells[col];

    switch (square.type) {
      case SquareType.Clues:
        cell.appendChild(Elements.clues(square.value));
        cell.classList.add('wc-clue-square');
        break;
      case SquareType.Number:
        numberSquare = <NumberModel>square;
        cell.setAttribute('data-number', numberSquare.number.toString());
        break;
      case SquareType.Block:
        cell.classList.add('wc-block');
        break;
      case SquareType.Symbol:
        cell.classList.add(square.value);
        break;
      case SquareType.Blank:
        cell.classList.add('wc-blank');
        break;
      default:
        break;
    }
  });

  return grid;
}

function _resize(root: HTMLElement, type: CrosswordType, {rows, cols}: GridSize,
                 squares: SquareListItem[]): void {
  let squareSize: number;

  if (type === CrosswordType.Regular) {
    squareSize = SQUARE_SIZE_REGULAR;
  } else if (type === CrosswordType.CluesInSquares) {
    squareSize = SQUARE_SIZE_CIS;
  } else {
    throw new TypeError('Invalid crossword type: ' + type + '.');
  }

  // Add buffer pixels at the end to avoid rows wrapping. This should
  // not be necessary, but has happened in the wild.
  // TODO: Attempt to remove the buffer pixels.
  root.style.height = toPixels(rows * (squareSize - BORDER_WIDTH) + 8);
  root.style.width = toPixels(cols * (squareSize - BORDER_WIDTH) + 8);
  root.style.flex = '0 0 ' + root.style.width;

  _getAllSquares(root).forEach((square: Square) => {
    square.style.height = toPixels(squareSize);
    square.style.width = toPixels(squareSize);

    if (square.classList.contains('wc-clue-square')) {
      square.style.fontSize = toPixels(BASE_FONT_SIZE);
    } else {
      square.style.fontSize = toPixels(squareSize * 0.65625);
      square.style.lineHeight = toPixels(squareSize);
    }
  });

  if (type === CrosswordType.CluesInSquares) {
    _resizeClueSquares(root, squares, squareSize);
  }
}

function _resizeClueSquares(root: HTMLElement, squares: SquareListItem[],
                            squareSize: number): void {
  const MIN_CLUE_RATIO: number = 0.2;
  const EPSILON: number = Math.pow(2, -52);
  const FONT_SIZES: number[] = range(MIN_FONT_SIZE, MAX_FONT_SIZE, 0.5).reverse();

  let isMinimum: (x: number) => boolean = (x: number) => {
    return Math.abs(x - MIN_CLUE_RATIO) < EPSILON;
  };

  let clueSquares: SquareListItem[] = squares.filter((s: SquareListItem) => {
    return s.square.type === SquareType.Clues;
  });

  clueSquares.forEach(({row, col, square}: SquareListItem) => {
    let numberOfClues: number = square.value.length;

    let sizes: number[] = square.value.map((clue: ClueModel) => clue.clueTextSize());
    let ratios: number[] = sizes.map((x: number) => Math.max(
        MIN_CLUE_RATIO,
        Math.round((x / sum(sizes)) * 1000) / 1000
      )
    );

    if (sum(ratios) > 1) {
      // Evenly distribute the surplus among all clues that are not minimum height
      let surplus: number = Math.abs(1 - sum(ratios));
      let minimumHeightClueCount: number = ratios.filter(isMinimum).length;
      let subtract: number = surplus / (numberOfClues - minimumHeightClueCount);

      for (let i: number = 0; i < ratios.length; i++) {
        if (!isMinimum(ratios[i]))
          ratios[i] = ratios[i] - subtract;
      }
    }

    _getCluesBySquare(root, row, col).forEach((clue: Clue, index: number) => {
      let text: ClueText;
      let fontSize: number = MAX_FONT_SIZE;
      let clueHeight: number = ratios[index] * SQUARE_SIZE_CIS;

      // Set clue height
      clue.style.height = toPixels(clueHeight);

      // Iteratively adjust clue font size
      text = <ClueText>clue.firstElementChild;
      if (text !== null && text.classList.contains('wc-text')) {
        let i: number = 0;

        text.style.fontSize = toPixels(fontSize);

        for (i; i < FONT_SIZES.length; i++) {
          if (text.clientHeight < (clueHeight - 2)) break;
          text.style.fontSize = toPixels(FONT_SIZES[i]);
        }

        for (i; i < FONT_SIZES.length; i++) {
          if (text.clientWidth <= SQUARE_SIZE_CIS) break;
          text.style.fontSize = toPixels(FONT_SIZES[i]);
        }
      }
    });
  });
}

module Elements {
  export function wrapper(): HTMLDivElement {
    return <HTMLDivElement>html('div', ['wc-crossword-grid']);
  }

  export function row(): HTMLDivElement {
    return <HTMLDivElement>html('div', ['wc-row']);
  }

  export function square(row: number, col: number): Square {
    let square: Square = <Square>html('div', ['wc-square']);
    square.setAttribute('data-row', row.toString());
    square.setAttribute('data-col', col.toString());
    return square;
  }

  export function clues(cs: ClueModel[]): Clues {
    let el: Clues = <Clues>html('div', ['wc-clues']);
    cs.forEach((c: ClueModel) => { el.appendChild(clue(c)); });
    return el;
  }

  export function clue(c: ClueModel): Clue {
    let el: Clue = <Clue>html('div', ['wc-clue']);
    el.setAttribute('data-id', c.id.toString());
    el.appendChild(text(c.clue));
    return el;
  }

  export function text(clue: string): ClueText {
    let el: ClueText = <ClueText>html('span', ['wc-text']);
    el.textContent = clue;
    return el;
  }
}
